import * as React from "react";
import {
  ColorNamesColorPresetModel,
  BlockTypes,
  ColorNamesBlockModel,
  ImageDirection,
  MediaSizeTypes,
  LayoutJsonModel,
} from "../../models/BlockTransportSystemJsonModel";
import { observer, inject } from "mobx-react";
import BlockKitStore from "../../stores/BlockKitStore";
import { TextAlignTypes } from "../../models/TextAlignTypes";
import BlockPreviewImageBox from "../BlockPreviewImageBox/BlockPreviewImageBox";
import BlockPreviewImageModel from "../../models/BlockPreviewImageModel";
import { v4 as uuidv4 } from "uuid";
import { UploadBlockModel } from "../../models/UploadBlockModel";
import { convertFileToFileMeta } from "../../modules/FileMeta";
import { convertFileMetasToImageFileSetMap } from "../../modules/ImageFileSet";
import { convertDatetime, getDateWithoutSeconds } from "../../utils/DateHelper";
import { CNSSetLayoutStyles } from "../../models/CNSSetLayoutStyles";
import { Button, CloseButton, Col, FloatingLabel, Form, FormGroup, Modal, Row, Spinner, Stack, ToggleButton } from "react-bootstrap";
import { useEffect, useState } from "react";
import { PresetGroups } from "../../constants/PresetConfig";
import { KioskTypes, PolarProductConfig, ProductCategoryTypes, productConfigs } from "../../constants/PolarProductList";
import { BlockPresetConfig, blockPresetConfigs, BlockPresetTypes } from "../../constants/BlockPresetConfig";
import StudioUserStore from "../../stores/StudioUserStore";
import { t } from "i18next";
import i18n from "../../../shared/internationalization/I18nSetting";
import { getBlockTypeConfigs } from "../../constants/BlockTypeConfig";

export interface IBlockCreateBoxProps {
  blockKitStore?: BlockKitStore;
  studioUserStore?: StudioUserStore;
  isShow: boolean;
  onClickClose: () => void;
}

const BlockCreateBox: React.FC<IBlockCreateBoxProps> = inject("blockKitStore", "studioUserStore")(observer(({
  blockKitStore,
  studioUserStore,
  isShow,
  onClickClose
}) => {
  const [enableUploadProgress, setEnableUploadProgress] = useState<boolean>(false);
  const [progressCurrentIdx, setProgressCurrentIdx] = useState<number>(0);
  const [progressTotalLength, setProgressTotalLength] = useState<number>(0);
  const [selectedLicenseId, setSelectedLicenseId] = useState<string>("none");
  const [categoryId, setCategoryId] = useState<string>("none");
  const [blockType, setBlockType] = useState<BlockTypes>(BlockTypes.none); // BlockTypes 기본값 필요
  const [mediaSize, setMediaSzie] = useState<string>(MediaSizeTypes.normal4x6);
  const [textAlignType, setTextAlignType] = useState<TextAlignTypes>(TextAlignTypes.none); // TextAlignTypes 기본값 필요
  const [layout, setLayout] = useState<LayoutJsonModel>();
  const [previewImageList, setPreviewImageList] = useState<BlockPreviewImageModel[]>([]);
  const [isLocalFileNameChecked, setIsLocalFileNameChecked] = useState<boolean>(false);
  const [releaseDate, setReleaseDate] = useState<Date>(getDateWithoutSeconds());
  const [expireDate, setExpireDate] = useState<Date>(new Date(2100, 11, 31));
  const [grade, setGrade] = useState<number | null>(null);
  const [dedicatedProducts, setDedicatedProducts] = useState<string[]>([]);
  const [presetGroup, setPresetGroup] = useState<PresetGroups>();
  const [presetType, setPresetType] = useState<BlockPresetTypes>();
  const [presetConfig, setPresetConfig] = useState<BlockPresetConfig>();
  const [productCategory, setProductCategory] = useState<ProductCategoryTypes>();
  const [kioskType, setKioskType] = useState<KioskTypes>();
  const [filtereProductConfigs, setFilteredProductConfigs] = useState<PolarProductConfig[]>();
  const [isWorking, setIsWorking] = useState<boolean>(false);

  const handleFileOnChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    // 파일 배열로 변환
    const files: File[] = Array.from(e.target.files);

    // 이름순 정렬 (한글 + 숫자 포함)
    files.sort((a, b) => b.name.localeCompare(a.name, "ko", { numeric: true }));

    const fileMetas = files.map((file) => convertFileToFileMeta(file));
    const imageFileSetMap = convertFileMetasToImageFileSetMap(fileMetas);

    const setsArray = Array.from(imageFileSetMap.values());

    // 순차 처리
    for (const set of setsArray) {
      if (!set.image) continue;

      const base64 = await new Promise<string | ArrayBuffer | null>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(set.image.file);
      });

      if (!base64) continue;

      const img = new Image();
      await new Promise<void>((resolve) => {
        img.onload = () => resolve();
        img.src = base64.toString();
      });

      const previewImage = {
        originFile: set.image.file,
        originUrl: base64.toString(),
        cutFile: set.cut?.file,
        height: img.height,
        width: img.width,
        description: "",
        name: isLocalFileNameChecked
          ? set.image.file.name.replace(/\.[^/.]+$/, "")
          : "",
        subjectName: "",
        priority: 3,
        id: uuidv4(),
        switchableBlockModel: {
          colorNamesThemeLayout: presetConfig?.colorNamesLayout ?? CNSSetLayoutStyles.NONE,
          presetModels: [],
        },
        imageDirection:
          img.width <= img.height
            ? ImageDirection.vertical
            : ImageDirection.horizontal,
      };

      setPreviewImageList((prev) => [...prev, previewImage]);
    }
  };

  // 공통 업데이트 함수
  const updatePreviewImage = (id: string, changes: Partial<BlockPreviewImageModel>) => {
    setPreviewImageList(prev =>
      prev.map((item, i) => (item.id === id ? { ...item, ...changes } : item))
    );
  };

  const deleteImageBtnClicked = (index: number) => {
    let tempPreviewImageList = [...previewImageList];
    tempPreviewImageList.splice(index, 1);
    setPreviewImageList(tempPreviewImageList);
  };

  const onLocalFileNameChecked = (isChecked: boolean) => {
    let tempPreviewImageList = [...previewImageList];
    setIsLocalFileNameChecked(!isLocalFileNameChecked);
    if (isChecked) {
      for (let i = 0; i < tempPreviewImageList.length; i++) {
        tempPreviewImageList[i].name = tempPreviewImageList[i].originFile.name.replace(/\.[^/.]+$/, "");
      }
    } else {
      for (let i = 0; i < tempPreviewImageList.length; i++) {
        tempPreviewImageList[i].name = "";
      }
    }
    setPreviewImageList(tempPreviewImageList);
  }

  const createAsync = async () => {
    if (!categoryId) {
      alert("카테고리를 입력해주세요.");
      return;
    }

    if (!selectedLicenseId) {
      alert("라이선스를 선택해주세요.");
      return;
    }

    if (
      !previewImageList ||
      previewImageList.length <= 0
    ) {
      alert("선택된 파일이 없습니다.");
      return;
    }

    if (dedicatedProducts?.length === 0) {
      alert("노출 제품을 1개 이상 선택해주세요.");
      return
    }

    // 이미지 리스트 우선순위 검증
    if (
      previewImageList.some(
        (item) =>
          item.priority === null ||
          !Number.isInteger(item.priority) ||
          item.priority <= 0
      )
    ) {
      alert("모든 이미지의 우선순위를 1이상의 숫자로 입력해주세요.");
      return;
    }

    if (
      isColorNamesType() &&
      previewImageList.some((list) => {
        return (
          (list.switchableBlockModel as ColorNamesBlockModel)?.presetModels
            ?.length <= 0
        );
      })
    ) {
      alert("컬러넴스 텍스트 색상 프리셋을 모두 채워주세요!");
      return;
    }
    if (
      !confirm(
        `${previewImageList.length}개의 이미지를 업로드하시겠습니까?`
      )
    ) {
      return;
    }

    const license = blockKitStore?.allLicenses?.find(
      (license) => license.id === selectedLicenseId
    );
    const category = blockKitStore?.allCategories?.find(
      (category) => category.id === categoryId
    );
    if (!category) {
      alert("유효하지 않은 카테고리 입니다.");
      return;
    }

    setEnableUploadProgress(true);

    let blockList: UploadBlockModel[] = [];
    for (let i = 0; i < previewImageList.length; i++) {
      const block = previewImageList[i];
      blockList.push({
        name: block.name.normalize("NFC"),
        subjectName: block.subjectName?.normalize("NFC") ?? "",
        tags: [],
        type: blockType,
        textAlignType: textAlignType as TextAlignTypes,
        layoutId: layout?.layoutId,
        layoutType: layout?.layoutType,
        categoryId: category.id.normalize("NFC"),
        category: category.name.normalize("NFC"),
        licenseId: license?.id.normalize("NFC"),
        license: license?.name.normalize("NFC"),
        description: block.description.normalize("NFC"),
        image: block.originFile,
        imageWidth: block.width,
        imageHeight: block.height,
        previewImage: block.previewFile,
        silverImage: block.silverFile,
        backOriginImage: block.backOriginFile,
        backSilverImage: block.backSilverFile,
        cutImage: block.cutFile,
        iconImage: block.iconFile,
        priority: block.priority,
        switchableBlockModel: block.switchableBlockModel,
        imageDirection: block.imageDirection,
        dedicatedProducts: dedicatedProducts || [],
        releaseDate: releaseDate,
        expireDate: expireDate,
        grade: grade || null,
        mediaSize: mediaSize,
      });
    }
    const newBlocks = await blockKitStore?.addNewImageBlocks(blockList, (cnt, total) => {
      setProgressCurrentIdx(cnt + 1);
      setProgressTotalLength(total);
    });

    if (newBlocks) {
      for (const newBlock of newBlocks) {
        await blockKitStore?.studioController.postBlockStagingAsync(newBlock);
      }
    }

    setEnableUploadProgress(false);
    setSelectedLicenseId("none");
    setPreviewImageList([]);
    setReleaseDate(getDateWithoutSeconds());
    setBlockType(BlockTypes.none);
    setGrade(null);
    setMediaSzie(MediaSizeTypes.normal4x6);


    alert("업로드가 완료되었습니다.");
  }

  const isColorNamesType = () => {
    return blockType === BlockTypes.colorNames;
  }

  const isUseTextColorPresets = () => {
    const selectedType = blockType.toString();
    return (
      selectedType === BlockTypes.colorNames ||
      selectedType === BlockTypes.graphicNames ||
      selectedType === BlockTypes.graphicNames12cut
    );
  }

  const pushTextColorPreset = (
    previewIndex: number,
    model: ColorNamesColorPresetModel
  ) => {
    let tempPreviewImageList = [...previewImageList];

    (tempPreviewImageList[previewIndex].switchableBlockModel as ColorNamesBlockModel)?.presetModels.push(model);
    setPreviewImageList(tempPreviewImageList);
  }

  const editTextColorPreset = (
    index: number,
    model: ColorNamesColorPresetModel,
    presetIndex: number
  ) => {
    let tempPreviewImageList = [...previewImageList];

    (tempPreviewImageList[index].switchableBlockModel as ColorNamesBlockModel).presetModels[presetIndex] = model;
    setPreviewImageList(tempPreviewImageList);
  }

  useEffect(() => {
    if (presetGroup && presetType) {
      const config = blockPresetConfigs.find(i => i.groups.includes(presetGroup) && i.presetType === presetType);
      setPresetConfig(config);
    } else {
      setPresetConfig(undefined);
    }
  }, [presetGroup, presetType]);

  useEffect(() => {
    const configs = productConfigs
      ?.filter(i => (!kioskType || i.kioskTypes.includes(kioskType)) && (!productCategory || i.productCategory === productCategory))
      ?.sort()
    setFilteredProductConfigs(configs);
  }, [kioskType, productCategory]);

  useEffect(() => {
    setBlockType(presetConfig?.blockType ?? BlockTypes.none);
    setTextAlignType(presetConfig?.defaultTextAlignType ?? TextAlignTypes.none);
  }, [presetConfig]);

  return (
    <Modal show={isShow} size="xl" onHide={() => onClickClose()}>
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body>
        <Form.Group as={Row}>
          <Col sm={4}>
            <Form.Group as={Row} className="mb-1">
              <Col sm={6}>
                <Form.Select
                  value={presetGroup}
                  onChange={(e) => setPresetGroup(e.target.value as PresetGroups)}
                >
                  <option value="" key="none">{t("preset-group-selection")}</option>
                  {studioUserStore?.me?.allowedPresets?.map((preset) => (
                    <option value={preset} key={preset}>
                      {preset}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col sm={6}>
                <Form.Select
                  value={presetType}
                  onChange={(e) => {
                    setPresetType(e.target.value as BlockPresetTypes);
                    const dedicatedProducts = blockPresetConfigs.find(i => i.presetType === e.target.value)?.dedicatedProducts;
                    if (dedicatedProducts) {
                      setDedicatedProducts(dedicatedProducts);
                    }
                  }}
                  disabled={!presetGroup}
                >
                  <option value="" key="none">{t("preset-selection")}</option>
                  {blockPresetConfigs
                    ?.filter(i => presetGroup && i.groups.includes(presetGroup))
                    ?.map((preset) => (
                      <option value={preset.presetType} key={preset.presetType}>
                        {preset.presetType}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            </Form.Group>
            <Form.Group hidden={!presetConfig}>
              <FormGroup as={Row} className="mb-1">
                <Col sm={11}>
                  <FloatingLabel label={`${t("category")} (${categoryId})`}>
                    <Form.Select
                      value={categoryId}
                      onChange={(e) => {
                        setCategoryId(e.target.value);
                      }}
                    >
                      <option value="none" key={"none"}>{t("category-selection-message")}</option>
                      {blockKitStore?.allCategories?.map((category) => {
                        return (
                          <option value={category.id} key={category.id}>
                            {`${category.lang.find(c => c.key === i18n.language)?.name || category?.name} (${category.blockCount}) [${category?.dedicatedProducts && (category.dedicatedProducts.length > 10
                              ? [...category.dedicatedProducts.slice(0, 10), "..."].join(", ")
                              : category.dedicatedProducts.join(", "))}]`}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </FloatingLabel>
                </Col>
                <CloseButton onClick={() => setCategoryId("none")} />
              </FormGroup>
              <FormGroup as={Row} className="mb-1">
                <Col sm={11}>
                  <FloatingLabel label={selectedLicenseId}>
                    <Form.Select
                      value={selectedLicenseId}
                      onChange={(e) => {
                        setSelectedLicenseId(e.target.value);
                      }}
                    >
                      <option value="none">{t("license-none")}</option>
                      {blockKitStore?.allLicenses?.map((license) => {
                        return (
                          <option value={license.id} key={license.id}>{license.name}</option>
                        );
                      })}
                    </Form.Select>
                  </FloatingLabel>
                </Col>
                <CloseButton onClick={() => setSelectedLicenseId("none")} />
              </FormGroup>
              <FormGroup className="mb-1" hidden={presetConfig?.isActivationDateConfigurable !== true}>
                <FloatingLabel label={`${t("release-date")} (${blockKitStore?.timeZone})`}>
                  <Form.Control
                    type="datetime-local"
                    onChange={(e) => {
                      setReleaseDate(new Date(e.target.value));
                    }}
                    value={convertDatetime(releaseDate, blockKitStore?.timeZone)}
                  />
                </FloatingLabel>
              </FormGroup>
              <FormGroup className="mb-1" hidden={presetConfig?.isActivationDateConfigurable !== true}>
                <FloatingLabel label={`${t("expire-date")} (${blockKitStore?.timeZone})`}>
                  <Form.Control
                    type="datetime-local"
                    onChange={(e) => {
                      setExpireDate(new Date(e.target.value));
                    }}
                    value={convertDatetime(expireDate, blockKitStore?.timeZone)}
                  />
                </FloatingLabel>
              </FormGroup>
              <FormGroup className="mb-1" hidden={presetConfig?.isBlockTypeConfigurable !== true}>
                <FloatingLabel label={t("block-type")}>
                  <Form.Select
                    value={blockType}
                    onChange={(e) => {
                      setBlockType(e.target.value as BlockTypes);
                    }}
                  >
                    {getBlockTypeConfigs().map((val) => {
                      return (
                        <option value={val.blockType} key={val.blockType}>{val.blockType}</option>
                      );
                    })}
                  </Form.Select>
                </FloatingLabel>
              </FormGroup>
              <FormGroup className="mb-1"
                hidden={!blockKitStore?.layouts?.filter(i => i.blockType === blockType)?.length || presetConfig?.isLayoutTypeConfigurable !== true}>
                <FloatingLabel label={`${t("layout")} (${layout?.layoutId})`}>
                  <Form.Select
                    value={layout?.layoutId}
                    onChange={(e) => {
                      const layout = blockKitStore?.layouts.find(i => i.layoutId === e.target.value);
                      setLayout(layout);
                    }}>
                    <option value={"none"} key={"none"}>{"none"}</option>
                    {blockKitStore?.layouts
                      ?.filter(i => i.blockType === blockType)
                      ?.map(i => {
                        return <option key={i.layoutId} value={i.layoutId}>{`${i.layoutType}`}</option>
                      })
                    }
                  </Form.Select>
                </FloatingLabel>
              </FormGroup>
              <FormGroup className="mb-1" hidden={presetConfig?.isMediaSizeConfigurable !== true}>
                <FloatingLabel label={t("media-size")}>
                  <Form.Select
                    value={mediaSize}
                    onChange={(e) => setMediaSzie(e.target.value)}
                  >
                    {Object.values(MediaSizeTypes).map((val, idx) => {
                      return (
                        <option value={val} key={idx}>{val}</option>
                      );
                    })}
                  </Form.Select>
                </FloatingLabel>
              </FormGroup>
              <FormGroup className="mb-1" hidden={presetConfig?.isTextAlignTypeConfigurable !== true}>
                <FloatingLabel label={t("text-align")}>
                  <Form.Select
                    value={textAlignType}
                    onChange={(e) => {
                      setTextAlignType(e.target.value as TextAlignTypes);
                    }}
                  >
                    {Object.keys(TextAlignTypes).map((val, idx) => {
                      return (
                        <option value={val} key={idx}>{val}</option>
                      );
                    })}
                  </Form.Select>
                </FloatingLabel>
              </FormGroup>
              <FormGroup className="mb-1" hidden={!!categoryId || (blockKitStore?.allCategories?.find(i => i.id === categoryId)?.probabilityTables?.length ?? 0) > 0}>
                <FloatingLabel label={t("probability-grade")}>
                  <Form.Select
                    value={grade || ""}
                    onChange={(e) => {
                      setGrade(e.target.value ? +e.target.value : null);
                    }}
                  >
                    <option value={1}>1</option>
                    <option value={2}>2</option>
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                    <option value={5}>5</option>
                  </Form.Select>
                </FloatingLabel>
              </FormGroup>
              <Form.Group>
                <Form.Group as={Row} className="mb-1" hidden={presetConfig?.isDedicatedProductsConfigurable !== true || !presetGroup || !presetType}>
                  <Form.Label>{t("dedicated-product")}</Form.Label>
                  <Col sm={4}>
                    <Form.Select
                      value={kioskType}
                      onChange={(e) => setKioskType(e.target.value as KioskTypes)}
                    >
                      <option value="" key="none">{t("kiosk-type-selection")}</option>
                      {Object.values(KioskTypes)?.map((type) => (
                        <option value={type} key={type}>
                          {type}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col sm={8} className="mb-1">
                    <Form.Select
                      value={productCategory}
                      onChange={(e) => setProductCategory(e.target.value as ProductCategoryTypes)}
                    >
                      <option value="" key="none">{t("product-category-selection")}</option>
                      {[...new Set(
                        productConfigs
                          ?.filter(i => kioskType && i.kioskTypes.includes(kioskType))
                          ?.map(product => product.productCategory)
                      )]
                        .map(category => (
                          <option value={category} key={category}>
                            {category}
                          </option>
                        ))}
                    </Form.Select>
                  </Col>
                  <Stack direction="horizontal" gap={1} className="flex-wrap">
                    {filtereProductConfigs?.map((value, index) => {
                      return (
                        <ToggleButton
                          variant={"outline-secondary"}
                          size="sm"
                          type="checkbox"
                          id={value.productType}
                          value={value.productType}
                          checked={dedicatedProducts?.includes(value.productType)}
                          onClick={() => {
                            const copiedProduct = [...dedicatedProducts];
                            const index = copiedProduct.indexOf(value.productType);
                            if (index < 0) {
                              copiedProduct.push(value.productType);
                              setDedicatedProducts(copiedProduct);
                            } else {
                              copiedProduct.splice(index, 1);
                              setDedicatedProducts(copiedProduct);
                            }
                          }}
                        >
                          {value.productType}
                        </ToggleButton>
                      );
                    })}
                  </Stack>
                </Form.Group>
                <Form.Group as={Row} className="mb-1" hidden={presetConfig?.isDedicatedProductsConfigurable === true}>
                  <Form.Label>{t("dedicated-product")}</Form.Label>
                  <Stack direction="horizontal" gap={1} className="flex-wrap">
                    {filtereProductConfigs
                      ?.filter(value => dedicatedProducts?.includes(value.productType))
                      ?.map((value, index) => {
                        return (
                          <ToggleButton
                            variant={"outline-secondary"}
                            size="sm"
                            type="checkbox"
                            id={value.productType}
                            value={value.productType}
                            checked={dedicatedProducts?.includes(value.productType)}
                          >
                            {value.productType}
                          </ToggleButton>
                        );
                      })}
                  </Stack>
                </Form.Group>
              </Form.Group>
            </Form.Group>
          </Col>
          <Col sm={8} hidden={!blockType || blockType === BlockTypes.none}>
            <Form.Check
              type="checkbox"
              label={t("upload-name-from-local-file")}
              onChange={(e) => onLocalFileNameChecked(e.target.checked)} />
            <Stack direction="horizontal" gap={2} className="flex-wrap" style={{ overflowY: "auto", height: "500px" }}>
              {previewImageList.map((previewImage, previewIndex) => (
                <BlockPreviewImageBox
                  key={previewImage.id}
                  previewImage={previewImage}
                  deleteBtnClicked={() =>
                    deleteImageBtnClicked(previewIndex)
                  }
                  isColorNamesType={isColorNamesType()}
                  blockType={blockType}
                  colorNamesLayout={(previewImage.switchableBlockModel as ColorNamesBlockModel)?.colorNamesThemeLayout}
                  isUseTextColorPresets={isUseTextColorPresets()}
                  presetConfig={presetConfig}
                  onPresetCreate={(previewModel: ColorNamesColorPresetModel) => {
                    pushTextColorPreset(previewIndex, previewModel);
                  }}
                  onChangeColorNamesLayout={(layout: CNSSetLayoutStyles) => updatePreviewImage(previewImage.id, {
                    switchableBlockModel: {
                      ...(previewImageList[previewIndex].switchableBlockModel as ColorNamesBlockModel),
                      colorNamesThemeLayout: layout,
                    } as ColorNamesBlockModel,
                  })}
                  onPresetDelete={(presetIndex: number) => {
                    let tempPreviewImageList = [...previewImageList];

                    (tempPreviewImageList[previewIndex].switchableBlockModel as ColorNamesBlockModel)?.presetModels.splice(presetIndex, 1);
                    setPreviewImageList(tempPreviewImageList);
                  }}
                  onPresetEdit={(presetIndex: number, previewModel: ColorNamesColorPresetModel) => {
                    editTextColorPreset(
                      previewIndex,
                      previewModel,
                      presetIndex
                    );
                  }}
                  updatePreviewImage={updatePreviewImage}
                />
              ))}
              <label htmlFor="selectFile">
                <div
                  style={{
                    margin: "15px",
                    verticalAlign: "center",
                    lineHeight: "190px",
                    textAlign: "center",
                    width: "400px",
                    height: "200px",
                    fontSize: "60px",
                    backgroundColor: "#fbfbfb",
                    color: "#000000",
                    border: "1px solid black",
                    borderColor: "#cccccc",
                  }}
                >
                  +
                </div>
                <input
                  id="selectFile"
                  style={{ display: "none" }}
                  type="file"
                  accept="image/jpg,image/png,image/jpeg,image/gif,image/svg+xml"
                  name="files"
                  onChange={(e) => {
                    handleFileOnChange(e);
                    e.target.value = null;
                  }}
                  multiple
                />
              </label>
            </Stack>
          </Col>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button
          disabled={enableUploadProgress}
          onClick={async () => {
            setIsWorking(true);
            try {
              await createAsync()
              onClickClose();

            } finally {
              setIsWorking(false);
            }
          }}>
          {isWorking &&
            <Spinner
              className="me-1"
              animation="border"
              size="sm"
              role="status"
            />}
          {enableUploadProgress ? `${t("button-uploading")} (${progressCurrentIdx}/${progressTotalLength})` : t("button-upload")}</Button>
        <Button disabled={isWorking} variant="secondary" onClick={() => onClickClose()}>{t("button-close")}</Button>
      </Modal.Footer>
    </Modal>
  );
}));

export default BlockCreateBox;
